import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CalendarClock, CheckCheck, UserMinus, XCircle, Briefcase } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, dateTimeOf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const typeIcon = {
  assigned: Briefcase,
  changed: CalendarClock,
  cancelled: XCircle,
  removed: UserMinus,
};

const typeTone = {
  assigned: "text-blue-600",
  changed: "text-amber-600",
  cancelled: "text-red-600",
  removed: "text-slate-500",
};

export const NotificationBell = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company } = useAuth();
  const readOnly = !!company?.demo_readonly;
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch (e) {
      console.error("Kunde inte hämta notiser", e);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  const open = async (n) => {
    if (!n.read) {
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    if (n.detail !== "deleted" && n.job_id) navigate(`/arbetsorder/${n.job_id}`);
  };

  const readAll = async () => {
    setItems((list) => list.map((x) => ({ ...x, read: true })));
    setUnread(0);
    api.post("/notifications/read-all").catch(() => {});
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell"
          aria-label={t("notif.title")}
          className="relative shrink-0 rounded-md p-2 text-slate-600 transition-colors duration-200 hover:bg-slate-100"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid="notification-menu" className="w-80 bg-white p-0">
        <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
          <span className="text-sm font-semibold text-slate-800">{t("notif.title")}</span>
          {unread > 0 && !readOnly && (
            <button
              data-testid="notification-read-all"
              onClick={readAll}
              className="flex items-center gap-1 text-xs font-medium text-blue-600 transition-colors duration-200 hover:text-blue-700"
            >
              <CheckCheck className="h-3.5 w-3.5" /> {t("notif.readAll")}
            </button>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-slate-500" data-testid="notification-empty">
              {t("notif.empty")}
            </p>
          )}
          {items.map((n) => {
            const Icon = typeIcon[n.type] || Bell;
            return (
              <button
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                onClick={() => open(n)}
                className={`flex w-full items-start gap-2.5 border-b border-slate-50 px-3 py-2.5 text-left transition-colors duration-200 hover:bg-slate-50 ${
                  n.read ? "bg-white" : "bg-blue-50/50"
                }`}
              >
                <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${typeTone[n.type] || "text-slate-500"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold uppercase tracking-[0.03em] text-slate-500">
                    {t(`notif.type.${n.type}`)}
                  </span>
                  <span className="block truncate text-sm font-medium text-slate-900">
                    {n.title}
                    {n.customer_name ? ` – ${n.customer_name}` : ""}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {n.start ? dateTimeOf(n.start) : dateTimeOf(n.created_at)}
                  </span>
                </span>
                {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-600" />}
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
